// ─── mic in ──────────────────────────────────────────────────────────────────
// getUserMedia → AudioWorklet → the input format the session declared. The DSP
// (resample + pcm16) is in `pcm.ts` and the turn detection is in `vad.ts`; this
// file is the Web Audio wiring and the transmission policy.
//
// Commit-driven providers use the local detector as a gate, with a short pre-roll so
// its rise time does not clip the first syllable. Native realtime providers instead
// receive the continuous timeline their own VAD expects. In that mode the local
// detector stays alive only for instant UI feedback and playback interruption.

import type { AudioFormat } from '@shared/protocol.ts'
import { analyseAudioFrame, type AcousticFrame } from './acoustics.ts'
import { MicEncoder } from './pcm.ts'
import { Vad, type VadOptions } from './vad.ts'
// The worklet is emitted as a same-origin, content-hashed asset. A package cannot
// rely on every app that consumes it copying a file into its own `public/` — one app
// forgetting is a dead microphone — so the audio thread's code travels with this
// module. `no-inline` matters: this file fits under Vite's default asset-inline limit,
// but a data URL would be rejected by the same policy. Keep this as an external URL,
// not a Blob made from `?raw`: AudioWorklet modules are governed by `script-src`, and
// a strict `script-src 'self'` correctly rejects Blob modules even when
// `worker-src blob:` is present.
import WORKLET_URL from './pcm-capture.worklet.js?url&no-inline'

export type TurnGate = 'vad' | 'ptt'

/**
 * Whether local turn detection is allowed to decide which microphone frames leave.
 *
 * Cloud realtime providers own their VAD and are designed to receive a continuous
 * stream. Commit-driven/local providers still need the original gated recording.
 * Keeping this separate from `TurnGate` lets a local detector remain useful for an
 * immediate playback cut without putting it in front of the model.
 */
export type TransmissionMode = 'gated' | 'continuous'

/** Pure policy seam: PTT is never made continuous by a provider setting. */
export function shouldTransmit(mode: TransmissionMode, gate: TurnGate, turnOpen: boolean): boolean {
  return gate === 'ptt' ? turnOpen : mode === 'continuous' || turnOpen
}

export interface MicHandlers {
  /** One encoded frame in the session format, according to the selected transmission mode. */
  onFrame(pcm: ArrayBuffer): void
  /** 0..1, every block, gate or no gate — this is what the level meter reads. */
  onLevel(level: number): void
  /** Aggregate acoustics only. `localSpeech` is the reversible browser VAD, not truth. */
  onAcoustics?(frame: AcousticFrame, localSpeech: boolean): void
  /** A turn boundary. In `ptt` mode these follow the key; in `vad` mode, the voice. */
  onEdge(edge: 'start' | 'stop'): void
  /** The browser revoked access or the selected input disappeared. Not fired by close(). */
  onEnded?(): void
}

export interface MicOptions {
  gate?: TurnGate
  /** Defaults to `gated`; realtime provider callers opt into `continuous` explicitly. */
  transmission?: TransmissionMode
  chunkMs?: number
  /** Frames kept back so an opening turn includes the audio just before it. */
  prerollFrames?: number
  vad?: VadOptions
}

/** Watch the authoritative device/permission-loss signal and return a cleanup. */
export function listenForMicrophoneEnd(stream: MediaStream, ended: () => void): () => void {
  const tracks = stream.getAudioTracks()
  for (const track of tracks) track.addEventListener('ended', ended)
  return () => {
    for (const track of tracks) track.removeEventListener('ended', ended)
  }
}

export class Microphone {
  private readonly encoder: MicEncoder
  private readonly vad: Vad
  private gate: TurnGate
  private transmission: TransmissionMode
  private held = false
  private preroll: ArrayBuffer[] = []
  private readonly prerollMax: number
  private closed = false
  private bytesSent = 0
  private blocksSeen = 0
  private readonly stopWatchingEnd: () => void

  private constructor(
    private readonly ctx: AudioContext,
    private readonly stream: MediaStream,
    private readonly source: MediaStreamAudioSourceNode,
    private readonly node: AudioWorkletNode,
    private readonly sink: GainNode,
    private readonly handlers: MicHandlers,
    format: AudioFormat,
    options: MicOptions,
  ) {
    if (format.encoding !== 'pcm16') throw new Error(`cannot capture ${format.encoding} audio`)
    this.encoder = new MicEncoder(ctx.sampleRate, format.sampleRate)
    this.vad = new Vad(options.vad)
    this.gate = options.gate ?? 'vad'
    this.transmission = options.transmission ?? 'gated'
    this.prerollMax = options.prerollFrames ?? 10
    this.node.port.onmessage = (e) => this.onBlock(e.data as Float32Array)
    this.stopWatchingEnd = listenForMicrophoneEnd(this.stream, this.onEnded)
  }

  static async open(
    ctx: AudioContext,
    format: AudioFormat,
    handlers: MicHandlers,
    options: MicOptions = {},
  ): Promise<Microphone> {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    })
    await ctx.audioWorklet.addModule(WORKLET_URL)
    const node = new AudioWorkletNode(ctx, 'pcm-capture', {
      numberOfInputs: 1,
      numberOfOutputs: 1,
      processorOptions: { chunkMs: options.chunkMs ?? 20 },
    })
    const source = ctx.createMediaStreamSource(stream)
    // The worklet writes no output; the muted sink is only there to guarantee the
    // graph keeps pulling it, which is not portable for a node with no destination.
    const sink = ctx.createGain()
    sink.gain.value = 0
    source.connect(node)
    node.connect(sink).connect(ctx.destination)
    return new Microphone(ctx, stream, source, node, sink, handlers, format, options)
  }

  setGate(gate: TurnGate): void {
    if (gate === this.gate) return
    this.gate = gate
    this.endTurn()
  }

  /**
   * Change only what is transmitted. The local VAD continues producing edges for UI
   * and immediate barge-in in both modes. Push-to-talk is always physically gated.
   */
  setTransmission(transmission: TransmissionMode): void {
    if (transmission === this.transmission) return
    this.transmission = transmission
    // Continuous mode already contains the lead-in; replaying the gated pre-roll on
    // top would duplicate the last 200 ms at the switch.
    if (transmission === 'continuous') this.preroll = []
  }

  /** Push-to-talk key state. Ignored in `vad` mode. */
  hold(held: boolean): void {
    if (this.gate !== 'ptt' || held === this.held) return
    this.held = held
    this.handlers.onEdge(held ? 'start' : 'stop')
    if (!held) this.preroll = []
  }

  get open(): boolean {
    return this.gate === 'ptt' ? this.held : this.vad.open
  }

  get sent(): number {
    return this.bytesSent
  }

  get gateMode(): TurnGate {
    return this.gate
  }

  get transmissionMode(): TransmissionMode {
    return this.transmission
  }

  /** Blocks seen from the worklet — zero means the capture graph never started. */
  get blocks(): number {
    return this.blocksSeen
  }

  /** The rate frames are actually sent at — the session's, not the device's. */
  get targetRate(): number {
    return this.encoder.toRate
  }

  get deviceRate(): number {
    return this.encoder.fromRate
  }

  /** Close any open turn without ending the session — mode switches, disconnects. */
  endTurn(notify = true): void {
    this.held = false
    this.preroll = []
    if (this.vad.reset() && notify) this.handlers.onEdge('stop')
  }

  close(): void {
    if (this.closed) return
    this.closed = true
    this.node.port.postMessage('stop')
    this.node.port.onmessage = null
    this.source.disconnect()
    this.node.disconnect()
    this.sink.disconnect()
    this.stopWatchingEnd()
    for (const track of this.stream.getTracks()) track.stop()
  }

  private readonly onEnded = () => {
    if (this.closed) return
    this.handlers.onEnded?.()
  }

  private onBlock(block: Float32Array): void {
    if (this.closed) return
    this.blocksSeen++
    const acoustics = analyseAudioFrame(block, this.ctx.sampleRate)
    const level = acoustics.rms
    this.handlers.onLevel(Math.min(1, level * 4)) // meters read better with headroom

    const wasOpen = this.open
    if (this.gate === 'vad') {
      const edge = this.vad.push(level, this.ctx.currentTime * 1000)
      if (edge) this.handlers.onEdge(edge)
    }
    this.handlers.onAcoustics?.(acoustics, this.open)

    // Resample every block, gate or no gate: the encoder is stateful, and skipping
    // blocks would make the resampler's carried position lie about elapsed time.
    const frame = this.encoder.encode(block).buffer as ArrayBuffer
    // A provider-owned VAD needs the complete timeline, including the silence in
    // which it decides the user is done. PTT remains gated regardless of this option.
    if (shouldTransmit(this.transmission, this.gate, this.open) && this.transmission === 'continuous') {
      this.preroll = []
      this.send(frame)
      return
    }
    if (!this.open) {
      this.preroll.push(frame)
      if (this.preroll.length > this.prerollMax) this.preroll.shift()
      return
    }
    if (!wasOpen && this.preroll.length) {
      for (const held of this.preroll) this.send(held)
      this.preroll = []
    }
    this.send(frame)
  }

  private send(frame: ArrayBuffer): void {
    this.bytesSent += frame.byteLength
    this.handlers.onFrame(frame)
  }
}
